/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useEffect } from 'react';
import { useUIDSeed } from 'react-uid';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import CloseIcon from '@material-ui/icons/Close';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Button from '@material-ui/core/Button';
import green from '@material-ui/core/colors/green';
import {
    bool, arrayOf, number, shape, string, func,
} from 'prop-types';

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        alignSelf: 'center',
    },
    dialogTitleText: {
        color: theme.palette.colorPrimary,
        fontWeight: 'bold',
    },
    solidColorDivider: {
        width: '100%',
        height: '5px',
        background: theme.palette.colorPrimary,
    },

    mainSectionContainer: {
        padding: '15px',
    },
    buttonContainer: {
        justifyContent: 'space-evenly',
        paddingTop: '25px',
        paddingBottom: '15px',
    },
    continueButton: {
        color: 'white',
        background: green[500],
        width: '100%',
        '&:hover': {
            background: green[700],
        },
    },
    cancelButton: {
        color: theme.palette.colorPrimary,
        width: '100%',
        border: `1px solid ${theme.palette.colorPrimary}`,
    },
    cancelButtonContainer: {
        width: '45%',
    },
    containueButtonContainer: {
        width: '45%',
    },
    inputField: {
        width: '100%',
    },
    closeIcon: {
        position: 'absolute',
        right: '15px',
        cursor: 'pointer',
    },
}));

const PersonalizationModal = ({
    isOpen, personalization: { lines, indicator }, setPersonalizationState, personalizationState, handlePersonalizationTextFieldChange, handlePersonalizationModalSubmit, closePersonalizationModal,
}) => {
    const classes = useStyles();
    const seed = useUIDSeed();
    useEffect(() => {
        // This essentially mimics componentDidMount and component did update
        // It runs whenever isOpen is switched to true.
        if (isOpen === true) {
            const lineStateArray = (lines || []).map((line) => ({
                lineText: '',
                maxChars: line.maxChars,
            }));
            const personalizationStateCopy = {
                ...personalizationState, modalOpen: true, lineState: lineStateArray, indicator,
            };

            setPersonalizationState(personalizationStateCopy);
        }
    }, [isOpen]);
    const continueButtonDisabled = (indicator === '3' && !personalizationState?.lineState?.some((line) => line.lineText.length > 0));
    return (
        <Dialog open={isOpen}>
            <DialogTitle className={classes.dialogTitle}>
                <Grid container>
                    <Grid item>
                        <Typography className={classes.dialogTitleText}>
                            PERSONALIZE YOUR GIFT
                        </Typography>
                    </Grid>
                    <Grid item>
                        <CloseIcon className={classes.closeIcon} onClick={closePersonalizationModal} />
                    </Grid>
                </Grid>
            </DialogTitle>
            <div className={classes.solidColorDivider} />
            <Grid container direction="column" className={classes.mainSectionContainer}>
                <Grid item>
                    <Typography>
                        Type your personalized message or name in the fields below.
                    </Typography>
                </Grid>
                {(lines || []).map((line, i) => (
                    <Grid item key={seed(line)}>
                        <TextField
                            label={`Line ${line.lineNumber}:`}
                            className={classes.inputField}
                            value={personalizationState?.lineState?.[i]?.lineText}
                            onChange={handlePersonalizationTextFieldChange}
                            name={`${i}`}
                        />
                    </Grid>
                ))}
                <Grid item container direction="row" className={classes.buttonContainer}>
                    <Grid item className={classes.cancelButtonContainer}>
                        <Button variant="outlined" className={classes.cancelButton} onClick={closePersonalizationModal}>
                            CANCEL
                        </Button>
                    </Grid>
                    <Grid item className={classes.containueButtonContainer}>
                        <Button
                            className={classes.continueButton}
                            onClick={handlePersonalizationModalSubmit}
                            disabled={continueButtonDisabled}
                        >
                            CONTINUE
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Dialog>
    );
};

PersonalizationModal.propTypes = {
    isOpen: bool.isRequired,
    personalization: shape({
        indicator: string.isRequired,
        lines: arrayOf(
            shape({
                lineNumber: number.isRequired,
                maxChars: number.isRequired,
            }).isRequired,
        ).isRequired,
    }).isRequired,
    setPersonalizationState: func.isRequired,
    handlePersonalizationModalSubmit: func.isRequired,
    handlePersonalizationTextFieldChange: func.isRequired,
    closePersonalizationModal: func.isRequired,
    personalizationState: shape({
        indicator: string,
        lineState: arrayOf(
            shape({
                lineNumber: number.isRequired,
                maxChars: number.isRequired,
            }).isRequired,
        ),
        modalOpen: bool.isRequired,
    }).isRequired,
};

export default PersonalizationModal;
